// Upload panel of the asset library page (creator dashboard and customer
// account). Each dropped .blend is presigned + PUT to S3, then staged as a
// library container upload — the shim detects every marked asset inside it. A
// Scanning placeholder row appears per file and resolves via the file-status
// polling loop in assets_table.js.
//
// The page element (#js-asset-library-page) configures the caps:
//   data-accepted-types   ".blend,.zip" (creator, default) or ".blend"
//   data-max-blend-bytes  per-.blend byte cap (default 4 GB)
import * as FilePond from "filepond"
import { presignAndUpload } from "../../../components/s3_upload/presign_upload"
import { displayFlashMessage, csrfToken } from "../../../util/methods"
import { autoRemoveCompletedUploads } from "../../../components/input_file/auto_remove_completed"
import { appendFileScanRow, startFilePolling } from "./assets_table"

var pond = null

var DEFAULT_BLEND_MAX_BYTES = 4 * 1024 * 1024 * 1024

function acceptedTypes(pageEl) {
  var raw =
    (pageEl && pageEl.getAttribute("data-accepted-types")) || ".blend,.zip"
  return raw.split(",").map(function (type) {
    return type.trim()
  })
}

function maxBlendBytes(pageEl) {
  var raw = parseInt(pageEl && pageEl.getAttribute("data-max-blend-bytes"), 10)
  return raw > 0 ? raw : DEFAULT_BLEND_MAX_BYTES
}

function humanMB(bytes) {
  return Math.round(bytes / (1024 * 1024)) + " MB"
}

$(document).on("turbolinks:load", function () {
  var panel = document.querySelector(".js-asset-upload-panel")
  if (!panel) return

  var pageEl = document.getElementById("js-asset-library-page")
  var accepted = acceptedTypes(pageEl)
  var zipAllowed = accepted.indexOf(".zip") !== -1
  var expects = accepted.join(" or ")

  pond = FilePond.create(panel.querySelector(".js-fileInputAsset"), {
    allowMultiple: true,
    allowRevert: false,
    allowPaste: false,
    // Pond-wide cap sized for .zip batches (R2 single-PUT limit is 5GB);
    // .blend files keep their own cap via the check in processAssetUpload.
    // Spelled in MB because FilePond's size parser doesn't understand "GB"
    // (it silently reads "4GB" as 4 bytes).
    maxFileSize: zipAllowed
      ? "4096MB"
      : humanMB(maxBlendBytes(pageEl)).replace(" ", ""),
    // Browsers report no MIME type for .blend, so the validate-type plugin
    // needs a detector that classifies the file itself; the result then
    // string-matches acceptedFileTypes (inherited from the input's accept attr).
    acceptedFileTypes: accepted,
    fileValidateTypeDetectType: function (source) {
      return classifyFile(source)
    },
    labelFileTypeNotAllowed: "Only " + expects + " files can be uploaded",
    fileValidateTypeLabelExpectedTypes: "Expects a " + expects + " file",
    labelIdle:
      "Drag & drop .blend files " +
      (zipAllowed ? "(or a library .zip) " : "") +
      'or <span class="filepond--label-action">Browse</span>',
    server: { process: processAssetUpload },
  })

  // Each upload becomes a Scanning row in the assets table, so the completed
  // bar has nothing left to say — clear it after a beat.
  autoRemoveCompletedUploads(pond)
})

// Turbolinks caches the FilePond-mutated DOM; without a destroy, restoring
// from cache (back/forward) and re-running turbolinks:load would pond the
// input a second time inside the cached wrapper.
$(document).on("turbolinks:before-cache", function () {
  if (pond) {
    pond.destroy()
    pond = null
  }
})

function detectExtension(name) {
  var lower = (name || "").toLowerCase()
  if (lower.endsWith(".blend")) return ".blend"
  if (lower.endsWith(".zip")) return ".zip"
  return "unknown"
}

// A creator's zip may wear a vanity extension (.paq etc.), so files that
// aren't named .blend/.zip classify by content: the zip local-file-header
// magic number counts as ".zip". The server does the same sniff, and
// ZipIngestJob verifies the archive for real.
function classifyFile(file) {
  var byName = detectExtension(file && file.name)
  if (byName !== "unknown") return Promise.resolve(byName)
  return startsWithZipMagic(file).then(function (isZip) {
    return isZip ? ".zip" : "unknown"
  })
}

function startsWithZipMagic(file) {
  if (!file || typeof file.slice !== "function") return Promise.resolve(false)
  return new Promise(function (resolve) {
    var reader = new FileReader()
    reader.onload = function () {
      var bytes = new Uint8Array(reader.result)
      resolve(
        bytes.length === 4 &&
          bytes[0] === 0x50 && // P
          bytes[1] === 0x4b && // K
          bytes[2] === 0x03 &&
          bytes[3] === 0x04
      )
    }
    reader.onerror = function () {
      resolve(false)
    }
    reader.readAsArrayBuffer(file.slice(0, 4))
  })
}

function processAssetUpload(fieldName, file, metadata, load, error) {
  var pageEl = document.getElementById("js-asset-library-page")
  var accepted = acceptedTypes(pageEl)

  classifyFile(file).then(function (extension) {
    if (accepted.indexOf(extension) === -1) {
      var expects = accepted.join(" or ")
      displayFlashMessage(
        "danger",
        file.name + " is not a " + expects + " file."
      )
      error("Not a " + expects + " file")
      return
    }

    // The pond-wide cap covers zips; individual .blend files have their own
    // cap (the server re-checks during the scan).
    var blendCap = maxBlendBytes(pageEl)
    if (extension === ".blend" && file.size > blendCap) {
      var advice =
        accepted.indexOf(".zip") !== -1
          ? " — zip it with its library instead."
          : "."
      displayFlashMessage(
        "danger",
        file.name + " is larger than " + humanMB(blendCap) + advice
      )
      error("File too large")
      return
    }

    uploadAndStage(file, pageEl, load, error)
  })
}

function uploadAndStage(file, pageEl, load, error) {
  presignAndUpload(file, "/s3/params")
    .then(function (cachedUpload) {
      return fetch(pageEl.getAttribute("data-files-create-url"), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          "X-CSRF-Token": csrfToken(),
        },
        body: JSON.stringify({ file: cachedUpload }),
      })
    })
    .then(function (response) {
      return response.json().then(function (json) {
        return { ok: response.ok, json: json }
      })
    })
    .then(function (result) {
      if (result.ok) {
        appendFileScanRow(result.json)
        startFilePolling()
        load(result.json.id)
      } else {
        var messages =
          (result.json.errors || [])
            .map(function (err) {
              return err.message
            })
            .join(" ") || "Upload failed."
        displayFlashMessage("danger", file.name + ": " + messages)
        error("Rejected")
      }
    })
    .catch(function () {
      displayFlashMessage(
        "danger",
        file.name + " failed to upload. Please try again."
      )
      error("Upload failed")
    })
}
